import React, { FC, useEffect, useState, useMemo } from 'react';
import _ from 'lodash';
import { Box, Text } from '@wix/design-system';
import { useTranslation } from '@wix/fe-essentials-standalone/i18n';
import { exploreResultsPageGenericClick } from '@wix/bi-logger-marketplace/v2';
import { useExploreContext } from '@components/Contexts/ExploreContext';
import { useBiSessionContext } from '@components/Contexts/BiSessionContext';
import { MIN_PRICE } from '@utils/constants';
import { ClearAll, updateQueryParams } from '@utils/route';
import { generateLocationLabel } from '@utils/locationLabel';
import { activeFilterCountExcludingBudget } from '@utils/filterUtils';
import { useBi } from '@components/Hooks/useBi';
import { useShallowRouter } from '@components/Hooks/useRouter';
import MobileFilterModalLayout from '../../../../MobileFilter/MobileFilterModalLayout';
import MobileBudgetFilter from '../../MobileBudget';
import { Pricing } from '@wix/ambassador-partners-profile-v1-partner-profile/types';
import MobileFilterMenuRow from '@components/MobileFilter/MobileFilterMenuRow';
import { menuSelectionKeys } from '../MobileFilterMenu';

type IMenuWithBudgetScreenProps = {
  onClose: () => void;
  clearWasClicked: ClearAll;
  setClearWasClicked: (x: ClearAll) => void;
  setOpenMobileFilter: (x: boolean) => void;
  setActiveRow: (x: keyof typeof menuSelectionKeys) => void;
};

const MenuWithBudgetScreen: FC<IMenuWithBudgetScreenProps> = ({
  onClose,
  clearWasClicked,
  setClearWasClicked,
  setOpenMobileFilter,
  setActiveRow,
}) => {
  const { sessionId } = useBiSessionContext();
  const {
    state: {
      selectedServices,
      selectedLanguages,
      selectedPricing,
      selectedLocation,
      origin,
      entry,
      currentPage,
      maxPrice,
    },
  } = useExploreContext();
  const { navigate, router } = useShallowRouter();
  const { t, i18n } = useTranslation();
  const logger = useBi();
  const numActiveFilters = useMemo(() => {
    if (clearWasClicked.all) {
      return 0;
    }
    return activeFilterCountExcludingBudget({
      selectedServices,
      selectedLanguages,
      selectedLocation,
    });
  }, [
    clearWasClicked.all,
    selectedServices,
    selectedLanguages,
    selectedLocation,
  ]);

  const [value, setValue] = useState({ ...selectedPricing });
  const [hasMax, setHasMax] = useState(!!selectedPricing.max);

  useEffect(() => {
    setValue({ ...selectedPricing });
    setHasMax(!!selectedPricing.max);

    if (!selectedPricing.max) {
      setValue({ min: selectedPricing.min, max: maxPrice });
    }
  }, [selectedPricing, maxPrice]);

  const hasBudget = useMemo(() => {
    return value.min !== MIN_PRICE || hasMax;
  }, [value.min, hasMax]);

  const filterData = {
    services: {
      dataHook: 'services-row',
      title: t('mobile-services-title'),
      onClick: () => {
        setActiveRow(menuSelectionKeys.SERVICES);
      },
      caption:
        _.size(selectedServices) === 0 ||
        clearWasClicked.all ||
        clearWasClicked.services
          ? t('mobile-filter-all-services-selected')
          : t('filter-items-selected', {
              selection: t(
                `explore.services.${selectedServices[0].displayKey}`,
              ),
              amount: Intl.NumberFormat(i18n.language, {}).format(
                _.size(selectedServices),
              ),
            }),
    },
    location: {
      dataHook: 'location-row',
      title: t('mobile-filter-menu-location'),
      onClick: () => {
        setActiveRow(menuSelectionKeys.LOCATION);
      },
      caption:
        _.isNil(selectedLocation) ||
        clearWasClicked.all ||
        clearWasClicked.location
          ? t('mobile-filter-anywhere-selected')
          : generateLocationLabel(selectedLocation.address ?? {}),
    },
    language: {
      dataHook: 'language-row',
      title: t('mobile-filter-communication-language'),
      onClick: () => {
        setActiveRow(menuSelectionKeys.LANGUAGE);
      },
      caption:
        _.size(selectedLanguages) === 0 ||
        clearWasClicked.all ||
        clearWasClicked.language
          ? t('mobile-filter-all-languages-selected')
          : t('filter-items-selected', {
              selection: selectedLanguages[0].value,
              amount: Intl.NumberFormat(i18n.language, {}).format(
                _.size(selectedLanguages),
              ),
            }),
    },
  };

  return (
    <MobileFilterModalLayout
      title={t('mobile-filter-menu-title')}
      onClose={() => {
        onClose();
      }}
      onClear={() => {
        setClearWasClicked({ ...clearWasClicked, all: true });
        setValue({ min: MIN_PRICE, max: maxPrice });
        setHasMax(false);
        logger.report(
          exploreResultsPageGenericClick({
            clickType: 'remove all filters',
            sessionId,
            origin,
            entry,
            exploreVersion: 'V2',
            data: JSON.stringify({ currentPage }),
          }),
        );
      }}
      onApply={() => {
        navigate(
          updateQueryParams(
            {
              min: value.min ?? undefined,
              max: hasMax ? value.max ?? undefined : undefined,
            },
            router,
            clearWasClicked,
          ),
          !clearWasClicked.all,
        );

        logger.report(
          exploreResultsPageGenericClick({
            clickType: 'apply budget filter',
            sessionId,
            origin,
            entry,
            data: JSON.stringify({ value, currentPage }),
            exploreVersion: 'V2',
          }),
        );
        setOpenMobileFilter(false);
      }}
      selectedAmount={hasBudget ? numActiveFilters + 1 : numActiveFilters || 0}
      content={
        <>
          <MobileFilterMenuRow filter={filterData.services} />
          <MobileFilterMenuRow filter={filterData.location} />
          <MobileFilterMenuRow filter={filterData.language} />
          <MobileBudgetFilterWrapper
            value={value}
            setValue={setValue}
            hasMax={hasMax}
            setHasMax={setHasMax}
          />
        </>
      }
    />
  );
};

export default MenuWithBudgetScreen;

interface IMobileBudgetFilterWrapperProps {
  value: Pricing;
  setValue: React.Dispatch<
    React.SetStateAction<{
      max?: number | undefined | null;
      min?: number | undefined | null;
    }>
  >;
  hasMax: boolean;
  setHasMax: React.Dispatch<React.SetStateAction<boolean>>;
}

const MobileBudgetFilterWrapper: FC<IMobileBudgetFilterWrapperProps> = ({
  value,
  setValue,
  hasMax,
  setHasMax,
}) => {
  const { t } = useTranslation();
  return (
    <Box
      paddingTop="18px"
      paddingLeft="24px"
      paddingRight="24px"
      direction="vertical"
    >
      <Text weight="normal">
        {t('mobile-filter-menu-starting-price-label')}
      </Text>
      <MobileBudgetFilter
        value={value}
        setValue={setValue}
        hasMax={hasMax}
        setHasMax={setHasMax}
      />
    </Box>
  );
};
